import { useEffect, useRef } from 'react'

import { createStreamHook } from '@/service/ws/use-web-socket-with-proto'
import { useWebSocketContext } from '@/service/ws/websocket-context'
import type {
  OrderbookHeatmapColumn,
  OrderbookHeatmapStream,
  ServerMessage,
} from '@/types/generated/public_api'
import type { WebSocketRequest } from '@/types/service'

/**
 * The open column from a live heatmap frame, if it is for this subscription. Every subscriber
 * shares the flat 'orderbookHeatmapStream' listener key, so frames for other instruments arrive
 * here too.
 */
export function extractLiveHeatmapColumn(
  message: ServerMessage,
  payload: OrderbookHeatmapStream
): OrderbookHeatmapColumn | undefined {
  const heatmap = message.data?.orderbookHeatmap
  if (!heatmap || heatmap.exchange !== payload.exchange || heatmap.symbol !== payload.symbol)
    return undefined
  return heatmap.columns.at(-1)
}

/**
 * The order-book heatmap's open column, about once a second while the book changes and nothing
 * while it is quiet. The first frame is sent on subscribe. One stream per instrument, shared by
 * every timeframe. A backend without it acks the subscribe with an empty channel and sends
 * nothing, which leaves the stored opening snapshot on screen.
 */
const useHeatmapStream = createStreamHook<OrderbookHeatmapStream, OrderbookHeatmapColumn>({
  channelKey: 'orderbookHeatmapStream',
  extractResponse: extractLiveHeatmapColumn,
  getPayloadDeps: (p) => [p.exchange, p.symbol],
  listenerChannelKey: 'orderbookHeatmapStream',
})

/** Replay after subscribing: a new wire subscription invalidates its old cache first. */
export function useOrderbookHeatmapStream(
  request: WebSocketRequest<OrderbookHeatmapStream, OrderbookHeatmapColumn>
) {
  const { getCachedHeatmapColumn } = useWebSocketContext()
  const result = useHeatmapStream(request)
  const onMessageRef = useRef(request.onMessage)
  useEffect(() => {
    onMessageRef.current = request.onMessage
  }, [request.onMessage])
  const { connected, payload } = request
  useEffect(() => {
    if (!connected) return
    const column = getCachedHeatmapColumn(payload.exchange, payload.symbol)
    if (column) onMessageRef.current?.(column)
  }, [connected, getCachedHeatmapColumn, payload.exchange, payload.symbol])
  return result
}
